import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,
  Users,
  UserPlus,
  Mail,
  Crown,
  Trash2,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Database,
  Loader2,
  FolderOpen,
  AlertTriangle
} from 'lucide-react';
import { fetchMetadataSchemas, updateMetadataSchemaType, type MetadataSchemaGroup } from '../api/segments';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { buildApiUrl } from '../config/api';

interface User {
  id: number;
  email: string;
  name: string;
  organizations: Organization[];
}

interface Organization {
  id: number;
  name: string;
  role: string;
  projects: Project[];
}

interface Project {
  id: number;
  name: string;
  api_key: string;
  organization: {
    id: number;
    name: string;
  };
}

interface Member {
  id: number;
  user: {
    id: number;
    name: string;
    email: string;
  };
  role: string;
  joined_at: string;
}

interface PendingInvitation {
  id: number;
  email: string;
  role: string;
  inviter: {
    name: string;
    email: string;
  };
  expires_at: string;
  created_at: string;
}

interface MembersResponse {
  members: Member[];
  pending_invitations: PendingInvitation[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

interface SettingsProps {
  user: User;
  token: string;
  currentProject: Project | null;
  onRefreshUser: () => void;
  onProjectDeleted?: () => void;
}

export default function Settings({ user, token, currentProject, onRefreshUser, onProjectDeleted }: SettingsProps) {
  const [userName, setUserName] = useState(user.name);
  const [orgName, setOrgName] = useState('');
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [updatingOrg, setUpdatingOrg] = useState(false);
  
  // Team management state
  const [members, setMembers] = useState<Member[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [sendingInvite, setSendingInvite] = useState(false);

  // Metadata schemas state
  const [metadataSchemas, setMetadataSchemas] = useState<MetadataSchemaGroup[]>([]);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [updatingSchemaId, setUpdatingSchemaId] = useState<number | null>(null);

  // Project deletion state
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState(false);

  // Get current organization
  const currentOrg = user.organizations[0];
  const isAdmin = currentOrg?.role === 'admin';
  
  // Initialize org name
  useEffect(() => {
    if (currentOrg) {
      setOrgName(currentOrg.name);
    }
  }, [currentOrg]);
  
  // Load team members
  useEffect(() => {
    if (currentOrg) {
      fetchMembers();
    }
  }, [currentOrg, currentPage]);

  // Load metadata schemas when project changes
  useEffect(() => {
    if (currentProject) {
      loadMetadataSchemas();
    }
  }, [currentProject]);

  const loadMetadataSchemas = async () => {
    if (!currentProject) return;

    try {
      setLoadingSchemas(true);
      const data = await fetchMetadataSchemas(currentProject.id, token);
      setMetadataSchemas(data);
    } catch (error) {
      console.error('Error loading metadata schemas:', error);
    } finally {
      setLoadingSchemas(false);
    }
  };

  const handleUpdateSchemaType = async (schemaId: number, newType: 'text' | 'enum' | 'number') => {
    if (!currentProject) return;

    try {
      setUpdatingSchemaId(schemaId);
      await updateMetadataSchemaType(currentProject.id, schemaId, newType, token);
      await loadMetadataSchemas();
    } catch (error) {
      console.error('Error updating schema type:', error);
    } finally {
      setUpdatingSchemaId(null);
    }
  };

  const handleClearOverride = async (schemaId: number) => {
    if (!currentProject) return;

    try {
      setUpdatingSchemaId(schemaId);
      await updateMetadataSchemaType(currentProject.id, schemaId, null as any, token);
      await loadMetadataSchemas();
    } catch (error) {
      console.error('Error clearing schema override:', error);
    } finally {
      setUpdatingSchemaId(null);
    }
  };
  
  const fetchMembers = async () => {
    if (!currentOrg) return;
    
    try {
      setLoadingMembers(true);
      const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}/members?page=${currentPage}&per_page=10`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch members');
      }
      
      const data = await response.json();
      const membersData: MembersResponse = data.data;
      
      setMembers(membersData.members);
      setPendingInvitations(membersData.pending_invitations);
      setTotalPages(membersData.pagination.total_pages);
      setTotalCount(membersData.pagination.total_count);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };
  
  const handleUpdateProfile = async () => {
    if (!userName.trim()) return;
    
    try {
      setUpdatingProfile(true);
      const response = await fetch(buildApiUrl('api/v1/profile'), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user: {
            name: userName.trim()
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update profile');
      }
      
      // Refresh user data
      onRefreshUser();
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setUpdatingProfile(false);
    }
  };
  
  const handleUpdateOrganization = async () => {
    if (!orgName.trim() || !currentOrg) return;
    
    try {
      setUpdatingOrg(true);
      const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organization: {
            name: orgName.trim()
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update organization');
      }
      
      // Refresh user data
      onRefreshUser();
    } catch (error) {
      console.error('Error updating organization:', error);
    } finally {
      setUpdatingOrg(false);
    }
  };
  
  const handleSendInvite = async () => {
    if (!inviteEmail.trim() || !currentOrg) return;
    
    try {
      setSendingInvite(true);
      const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}/invitations`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invitation: {
            email: inviteEmail.trim(),
            role: inviteRole
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send invitation');
      }
      
      // Reset form and close modal
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteModal(false);
      
      // Refresh members list
      fetchMembers();
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert(error instanceof Error ? error.message : 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };
  
  const handleUpdateMemberRole = async (memberId: number, newRole: string) => {
    if (!currentOrg) return;
    
    try {
      const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}/members/${memberId}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          role: newRole
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update member role');
      }
      
      // Refresh members list
      fetchMembers();
    } catch (error) {
      console.error('Error updating member role:', error);
    }
  };
  
  const handleRemoveMember = async (memberId: number) => {
    if (!currentOrg) return;
    
    const confirmed = window.confirm('Are you sure you want to remove this member?');
    if (!confirmed) return;
    
    try {
      const response = await fetch(buildApiUrl(`api/v1/organizations/${currentOrg.id}/members/${memberId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove member');
      }
      
      // Refresh members list
      fetchMembers();
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get all projects from current org
  const orgProjects = currentOrg?.projects || [];
  const canDeleteProjects = orgProjects.length > 1;

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    
    try {
      setDeletingProject(true);
      const response = await fetch(buildApiUrl(`api/v1/projects/${projectToDelete.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete project');
      }
      
      // Close modal
      setProjectToDelete(null);
      
      // Refresh user data to update project list
      onRefreshUser();
      
      // Notify parent if callback provided
      if (onProjectDeleted) {
        onProjectDeleted();
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete project');
    } finally {
      setDeletingProject(false);
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center space-x-2 mb-6 text-neutral-800">
        <SettingsIcon className="h-6 w-6 mr-1" />
        <h1 className="text-2xl font-bold flex items-center">Settings</h1>
      </div>
      
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="organization">Organization</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="metadata">Metadata</TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile" className="space-y-4">
          <Card
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            <CardHeader>
              <CardTitle>Profile Settings</CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="user-name">Your Name</Label>
                <Input
                  id="user-name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  disabled={updatingProfile}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="user-email">Email Address</Label>
                <Input
                  id="user-email"
                  value={user.email}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed
                </p>
              </div>
              
              <Button 
                onClick={handleUpdateProfile}
                disabled={!userName.trim() || updatingProfile || userName === user.name}
              >
                {updatingProfile ? 'Updating...' : 'Update Profile'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="organization" className="space-y-4">
          <Card
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            <CardHeader>
              <CardTitle>Organization Settings</CardTitle>
              <CardDescription>
                Manage your organization details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  disabled={updatingOrg || !isAdmin}
                />
                {!isAdmin && (
                  <p className="text-xs text-muted-foreground">
                    Only admins can change the organization name
                  </p>
                )}
              </div>
              
              {isAdmin && (
                <Button 
                  onClick={handleUpdateOrganization}
                  disabled={!orgName.trim() || updatingOrg || orgName === currentOrg?.name}
                >
                  {updatingOrg ? 'Updating...' : 'Update Organization'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Projects Management - Admin Only */}
          {isAdmin && (
            <Card
              className="card-premium-hover"
              style={{
                opacity: 0,
                animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 100ms forwards',
              }}
            >
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FolderOpen className="h-5 w-5" />
                  <span>Projects</span>
                  <Badge variant="secondary">{orgProjects.length}</Badge>
                </CardTitle>
                <CardDescription>
                  Manage your organization's projects. Deleting a project will permanently remove all its events, clusters, and journeys.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {orgProjects.map((project, index) => (
                    <div 
                      key={project.id} 
                      className="flex items-center justify-between p-3 border rounded-lg"
                      style={{
                        opacity: 0,
                        animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${150 + index * 50}ms forwards`,
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <FolderOpen className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{project.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {project.api_key.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {currentProject?.id === project.id && (
                          <Badge variant="outline">Current</Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setProjectToDelete(project)}
                          disabled={!canDeleteProjects}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title={!canDeleteProjects ? "Cannot delete the last project" : "Delete project"}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {!canDeleteProjects && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      You must have at least one project. Create a new project before deleting this one.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="team" className="space-y-4">
          <Card
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span>Team Members</span>
                  <Badge variant="secondary">{totalCount}</Badge>
                </div>
                {isAdmin && (
                  <Button onClick={() => setShowInviteModal(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite Member
                  </Button>
                )}
              </CardTitle>
              <CardDescription>
                Manage your team members and their roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingMembers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Loading members...</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Active Members */}
                  <div className="space-y-2">
                    {members.map((member, index) => (
                      <div 
                        key={member.id} 
                        className="flex items-center justify-between p-3 border rounded-lg"
                        style={{
                          opacity: 0,
                          animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${100 + index * 50}ms forwards`,
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">
                              {member.user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium">{member.user.name}</div>
                            <div className="text-sm text-muted-foreground">{member.user.email}</div>
                            <div className="text-xs text-muted-foreground">
                              Joined {formatDate(member.joined_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
                            {member.role === 'admin' && <Crown className="h-3 w-3 mr-1" />}
                            {member.role}
                          </Badge>
                          {isAdmin && member.user.id !== user.id && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => handleUpdateMemberRole(member.id, member.role === 'admin' ? 'member' : 'admin')}
                                >
                                  {member.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleRemoveMember(member.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Remove Member
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pending Invitations */}
                  {pendingInvitations.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">Pending Invitations</h4>
                      {pendingInvitations.map((invitation, index) => (
                        <div 
                          key={invitation.id} 
                          className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                          style={{
                            opacity: 0,
                            animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${100 + (members.length + index) * 50}ms forwards`,
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <div className="font-medium">{invitation.email}</div>
                              <div className="text-sm text-muted-foreground">
                                Invited by {invitation.inviter.name} on {formatDate(invitation.created_at)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Expires {formatDate(invitation.expires_at)}
                              </div>
                            </div>
                          </div>
                          <Badge variant="outline">{invitation.role}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalCount)} of {totalCount} members
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm">
                          {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metadata" className="space-y-4">
          <Card
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Database className="h-5 w-5" />
                <span>Metadata Field Types</span>
              </CardTitle>
              <CardDescription>
                Configure how metadata fields are treated. Text fields are semantically clustered, enum fields are used for segmentation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!currentProject ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Select a project to manage metadata fields</p>
                </div>
              ) : loadingSchemas ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : metadataSchemas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="font-medium">No metadata fields detected yet</p>
                  <p className="text-sm mt-1">
                    Start sending events with metadata to see field type classifications
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {metadataSchemas.map((group, groupIndex) => (
                    <div 
                      key={group.event_name} 
                      className="space-y-3"
                      style={{
                        opacity: 0,
                        animation: `cardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${groupIndex * 100}ms forwards`,
                      }}
                    >
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <Badge variant="outline">{group.event_name}</Badge>
                      </h4>
                      <div className="space-y-2 ml-4">
                        {group.fields.map((field, fieldIndex) => (
                          <div
                            key={field.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                            style={{
                              opacity: 0,
                              animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${groupIndex * 100 + 50 + fieldIndex * 40}ms forwards`,
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <code className="text-sm bg-muted px-2 py-1 rounded">
                                {field.key}
                              </code>
                              {field.user_override_type && (
                                <Badge variant="secondary" className="text-xs">
                                  overridden
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Select
                                value={field.effective_type}
                                onValueChange={(value) =>
                                  handleUpdateSchemaType(field.id, value as 'text' | 'enum' | 'number')
                                }
                                disabled={updatingSchemaId === field.id}
                              >
                                <SelectTrigger className="w-32">
                                  {updatingSchemaId === field.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <SelectValue />
                                  )}
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">
                                    <span className="flex items-center gap-2">
                                      Text
                                      <span className="text-xs text-muted-foreground">(cluster)</span>
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="enum">
                                    <span className="flex items-center gap-2">
                                      Enum
                                      <span className="text-xs text-muted-foreground">(segment)</span>
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="number">
                                    <span className="flex items-center gap-2">
                                      Number
                                      <span className="text-xs text-muted-foreground">(aggregate)</span>
                                    </span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              {field.user_override_type && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleClearOverride(field.id)}
                                  disabled={updatingSchemaId === field.id}
                                  className="text-xs"
                                >
                                  Reset
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card
            className="card-premium-hover"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) 200ms forwards',
            }}
          >
            <CardHeader>
              <CardTitle className="text-base">How it works</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>
                <strong>Text:</strong> Free-form content that gets semantically clustered into topics
                (e.g., user prompts, questions, feedback)
              </p>
              <p>
                <strong>Enum:</strong> Categorical values used for segmentation and filtering
                (e.g., platform, language, plan tier)
              </p>
              <p>
                <strong>Number:</strong> Numeric values for aggregation and filtering
                (e.g., response time, token count)
              </p>
              <p className="pt-2 border-t">
                Field types are auto-detected when you first send an event. Override them here if the detection was incorrect.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Member Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your organization
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="teammate@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={sendingInvite}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole} disabled={sendingInvite}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowInviteModal(false)}
              disabled={sendingInvite}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendInvite}
              disabled={!inviteEmail.trim() || sendingInvite}
            >
              {sendingInvite ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Project Confirmation Dialog */}
      <AlertDialog open={!!projectToDelete} onOpenChange={(open: boolean) => !open && setProjectToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete Project
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to delete <strong>{projectToDelete?.name}</strong>?
              </p>
              <p className="text-red-600 font-medium">
                This action cannot be undone. All events, clusters, journeys, and metadata associated with this project will be permanently deleted.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingProject}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={deletingProject}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deletingProject ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Project'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 